import supabase from '../supabase/supabase'

/**
 * ✅ ECHTE API für Produkte mit Supabase
 * Ersetzt die Mock-API in src/services/api.js
 */

// =====================================================
// PRODUCTS API
// =====================================================

/**
 * Suche Produkte mit Filtern und Pagination
 */
export const searchProducts = async (query = '', params = {}) => {
  try {
    const {
      category,
      priceRange = [0, 100000],
      minRating = 0,
      inStock = false,
      tradeAssurance = false,
      verified = false,
      sortBy = 'relevance',
      page = 1,
      limit = 24,
      lang = 'de'
    } = params

    let queryBuilder = supabase
      .from('products_1737381234567')
      .select(`
        *,
        category:product_categories_1737381234567(slug, name_${lang}),
        supplier:suppliers_1737381234567(name, rating, years_in_business, is_verified),
        images:product_images_1737381234567(image_url, is_primary)
      `, { count: 'exact' })
      .eq('is_active', true)

    // Text search (Volltextsuche via tsvector)
    if (query && query !== 'all' && query !== 'alle') {
      queryBuilder = queryBuilder.textSearch('search_vector', query, {
        type: 'websearch',
        config: lang === 'de' ? 'german' : lang === 'fr' ? 'french' : 'english'
      })
    }

    // Filters
    if (category && category !== 'all') {
      queryBuilder = queryBuilder.eq('category.slug', category)
    }

    if (priceRange) {
      queryBuilder = queryBuilder
        .gte('price', priceRange[0])
        .lte('price', priceRange[1])
    }

    if (minRating > 0) {
      queryBuilder = queryBuilder.gte('rating', minRating)
    }

    if (inStock) {
      queryBuilder = queryBuilder.eq('in_stock', true)
    }

    if (tradeAssurance) {
      queryBuilder = queryBuilder.eq('trade_assurance', true)
    }

    if (verified) {
      queryBuilder = queryBuilder.eq('is_verified', true)
    }

    // Sorting
    switch (sortBy) {
      case 'price_asc':
        queryBuilder = queryBuilder.order('price', { ascending: true })
        break
      case 'price_desc':
        queryBuilder = queryBuilder.order('price', { ascending: false })
        break
      case 'rating':
        queryBuilder = queryBuilder.order('rating', { ascending: false })
        break
      case 'newest':
        queryBuilder = queryBuilder.order('created_at', { ascending: false })
        break
      default: // relevance
        if (query && query !== 'all') {
          // Bei Textsuche nach Relevanz sortieren
          queryBuilder = queryBuilder.order('rating', { ascending: false })
        } else {
          queryBuilder = queryBuilder.order('is_featured', { ascending: false })
        }
    }

    // Pagination
    const from = (page - 1) * limit
    const to = from + limit - 1
    queryBuilder = queryBuilder.range(from, to)

    const { data, error, count } = await queryBuilder

    if (error) throw error

    // Transform data (Bilder formatieren)
    const products = data.map(product => ({
      ...product,
      name: product[`name_${lang}`] || product.name_en,
      description: product[`description_${lang}`] || product.description_en,
      image: product.images.find(img => img.is_primary)?.image_url || product.images[0]?.image_url,
      images: product.images.map(img => img.image_url)
    }))

    return {
      products,
      total: count || 0,
      page,
      totalPages: Math.ceil((count || 0) / limit),
      hasMore: (from + products.length) < (count || 0)
    }

  } catch (error) {
    console.error('Search failed:', error)
    return {
      products: [],
      total: 0,
      page: 1,
      totalPages: 0,
      hasMore: false
    }
  }
}

/**
 * Lade einzelnes Produkt
 */
export const getProduct = async (id, lang = 'de') => {
  try {
    const { data, error } = await supabase
      .from('products_1737381234567')
      .select(`
        *,
        category:product_categories_1737381234567(slug, name_${lang}),
        supplier:suppliers_1737381234567(*),
        images:product_images_1737381234567(image_url, alt_text, is_primary)
      `)
      .eq('id', id)
      .eq('is_active', true)
      .single()

    if (error) throw error

    // Increment views
    await supabase.rpc('increment_product_views', { product_id: id })

    return {
      ...data,
      name: data[`name_${lang}`] || data.name_en,
      description: data[`description_${lang}`] || data.description_en,
      image: data.images.find(img => img.is_primary)?.image_url || data.images[0]?.image_url,
      images: data.images.map(img => img.image_url)
    }

  } catch (error) {
    console.error('Get product failed:', error)
    return null
  }
}

/**
 * Featured Produkte für Homepage
 */
export const getFeaturedProducts = async (limit = 8, lang = 'de') => {
  try {
    const { data, error } = await supabase
      .from('products_1737381234567')
      .select(`
        *,
        category:product_categories_1737381234567(slug, name_${lang}),
        supplier:suppliers_1737381234567(name, rating, years_in_business),
        images:product_images_1737381234567(image_url, is_primary)
      `)
      .eq('is_active', true)
      .eq('is_featured', true)
      .order('rating', { ascending: false })
      .limit(limit)

    if (error) throw error

    const products = data.map(product => ({
      ...product,
      name: product[`name_${lang}`] || product.name_en,
      description: product[`description_${lang}`] || product.description_en,
      image: product.images.find(img => img.is_primary)?.image_url || product.images[0]?.image_url,
      images: product.images.map(img => img.image_url)
    }))

    return { products }

  } catch (error) {
    console.error('Get featured products failed:', error)
    return { products: [] }
  }
}

// =====================================================
// ADMIN API (Produkte verwalten)
// =====================================================

/**
 * Neues Produkt erstellen
 */
export const createProduct = async (productData) => {
  try {
    const { data, error } = await supabase
      .from('products_1737381234567')
      .insert([productData])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }

  } catch (error) {
    console.error('Create product failed:', error)
    return { data: null, error }
  }
}

/**
 * Produkt aktualisieren
 */
export const updateProduct = async (id, updates) => {
  try {
    const { data, error } = await supabase
      .from('products_1737381234567')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }

  } catch (error) {
    console.error('Update product failed:', error)
    return { data: null, error }
  }
}

/**
 * Produkt löschen (Soft-Delete)
 */
export const deleteProduct = async (id) => {
  try {
    const { error } = await supabase
      .from('products_1737381234567')
      .update({ is_active: false })
      .eq('id', id)

    if (error) throw error
    return { error: null }

  } catch (error) {
    console.error('Delete product failed:', error)
    return { error }
  }
}

/**
 * Produkt-Bilder hochladen
 */
export const uploadProductImage = async (productId, file) => {
  try {
    const fileExt = file.name.split('.').pop()
    const fileName = `${productId}/${Date.now()}.${fileExt}`
    const filePath = `products/${fileName}`

    // Upload zu Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(filePath, file)

    if (uploadError) throw uploadError

    // Public URL generieren
    const { data: { publicUrl } } = supabase.storage
      .from('product-images')
      .getPublicUrl(filePath)

    // Bild-Eintrag in DB erstellen
    const { data, error } = await supabase
      .from('product_images_1737381234567')
      .insert([{
        product_id: productId,
        image_url: publicUrl,
        alt_text: file.name
      }])
      .select()
      .single()

    if (error) throw error
    return { data, error: null }

  } catch (error) {
    console.error('Upload image failed:', error)
    return { data: null, error }
  }
}

// =====================================================
// CATEGORIES API
// =====================================================

export const getCategories = async (lang = 'de') => {
  try {
    const { data, error } = await supabase
      .from('product_categories_1737381234567')
      .select('*')
      .eq('is_active', true)
      .order('display_order')

    if (error) throw error

    return data.map(cat => ({
      ...cat,
      name: cat[`name_${lang}`] || cat.name_en
    }))

  } catch (error) {
    console.error('Get categories failed:', error)
    return []
  }
}

// =====================================================
// SUPPLIERS API
// =====================================================

export const getSuppliers = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('suppliers_1737381234567')
      .select('*')
      .order('rating', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data

  } catch (error) {
    console.error('Get suppliers failed:', error)
    return []
  }
}